import React, { FC, useState } from 'react';
import { Box, Typography } from '@mui/material';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import { FormField, RatingOptions } from '../../models/training.models';
import { palette } from '../../theme';

interface RatingFieldComponentProps {
  field: FormField;
  value: number | null;
  onChange: (value: number) => void;
  disabled?: boolean;
}

const RatingFieldComponent: FC<RatingFieldComponentProps> = ({ field, value, onChange, disabled = false }) => {
  const [hoveredValue, setHoveredValue] = useState<number | null>(null);

  const options = field.options as RatingOptions;
  const min = options?.min || 1;
  const max = options?.max || 5;

  const stars = Array.from({ length: max - min + 1 }, (_, i) => i + min);

  const handleClick = (rating: number) => {
    if (!disabled) {
      onChange(rating);
    }
  };

  const handleMouseEnter = (rating: number) => {
    if (!disabled) {
      setHoveredValue(rating);
    }
  };

  const handleMouseLeave = () => {
    setHoveredValue(null);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        {field.name}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        {stars.map((rating) => {
          const isSelected = value !== null && rating <= value;
          const isHovered = hoveredValue !== null && rating <= hoveredValue;
          const shouldFill = isSelected || isHovered;

          return (
            <Box
              key={rating}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.5 : 1,
              }}
              onClick={() => handleClick(rating)}
              onMouseEnter={() => handleMouseEnter(rating)}
              onMouseLeave={handleMouseLeave}
            >
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {rating}
              </Typography>
              {shouldFill ? (
                <StarIcon sx={{ fontSize: 40, color: palette.primary.main }} />
              ) : (
                <StarBorderIcon sx={{ fontSize: 40, color: palette.text.primary }} />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default RatingFieldComponent;
